/**
 * Résout une URL d'asset (relative ou absolue) en la rattachant au backend.
 *
 * Problème visé:
 * - si on met "/downloadSvg?filename=..." dans un <img src>, le navigateur appelle l'origin du FRONT
 * - en prod Railway, ça devient https://<front>/downloadSvg... au lieu du backend
 */
export function getBackendBaseUrl(): string {
    // 1) Runtime env (env.js chargé avant main.ts)
    const runtime = (typeof window !== 'undefined' ? window.env?.VITE_BACK_URL : undefined) as string | undefined;

    // 2) Build-time env (fallback)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const built = (import.meta as any)?.env?.VITE_BACK_URL as string | undefined;

    const raw = (runtime || built || '').trim();
    if (!raw) {
        return '';
    }

    let url = raw;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = `https://${url}`;
    }

    // enlever les "/" finaux pour éviter les doubles slashes
    url = url.replace(/\/+$/, '');
    return url;
}

export function resolveUrlWithBackend(pathOrUrl: string): string {
    const value = (pathOrUrl || '').trim();
    if (!value) {
        return '';
    }

    // Déjà absolu => ne pas toucher
    if (value.startsWith('http://') || value.startsWith('https://')) {
        return value;
    }

    const base = getBackendBaseUrl();

    // Pas de backend configuré => fallback (reste relatif)
    if (!base) {
        return value.startsWith('/') ? value : `/${value}`;
    }

    // Joindre proprement
    return `${base}${value.startsWith('/') ? '' : '/'}${value}`;
}

export function buildDownloadSvgUrl(filename: string | undefined | null): string {
    if (!filename) {
        return '';
    }
    return resolveUrlWithBackend(`/downloadSvg?filename=${filename}`);
}

/**
 * Accepte soit:
 * - une key/chemin de fichier (ex: "2024/07/10/xxx.obj")
 * - un path endpoint (ex: "/downloadSvg?filename=...")
 * - une URL absolue
 * et renvoie une URL absolue pointant sur le backend.
 */
export function resolveDownloadSvg(value: string | undefined | null): string {
    if (!value) {
        return '';
    }
    const v = value.trim();
    if (!v) {
        return '';
    }

    if (v.startsWith('http://') || v.startsWith('https://')) {
        return v;
    }

    // Déjà un endpoint
    if (v.startsWith('/downloadSvg?') || v.startsWith('downloadSvg?')) {
        return resolveUrlWithBackend(v.startsWith('/') ? v : `/${v}`);
    }

    return buildDownloadSvgUrl(v);
}
